import { z } from 'zod/v3';

// ============================================================================
// 공통 Enum 스키마
// ============================================================================

/** 결제수단 */
export const paymentMethodTypeSchema = z.enum([
  'WPAY',
  'INICIS_CARD',
  'INICIS_VACCT',
  'KPN_NAVER',
  'KPN_NAVER_CARD',
  'KPN_NAVER_POINT',
  'KPN_CARD',
  'KPN_VACCT',
  'KPN_E_COUPON_CARD',
  'POINT',
  'PAY_LATER',
  'NONE',
]);
export type PaymentMethodType = z.infer<typeof paymentMethodTypeSchema>;

/** 주문 상태 */
export const orderStatusSchema = z.enum([
  'NONE',
  'AWAITING',
  'FAILED',
  'COMPLETED',
]);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

/** 상품 주문 클레임 상태 */
export const productOrderClaimStatusSchema = z.enum([
  'NONE',
  'REQUEST',
  'PROGRESS',
  'COMPLETED',
  'COMPLETED_WITH_REFUND_REQUESTED',
  'COMPLETED_WITH_REFUND_FAILED',
  'COMPLETED_WITH_REFUNDED',
  'RESTORED',
  'CANCELLATION_REQUESTED',
  'CANCELLATION_RESTORED',
  'CANCELLATION_WITH_REFUND_REQUESTED',
  'CANCELLATION_WITH_REFUND_FAILED',
  'CANCELLATION_WITH_REFUNDED',
]);
export type ProductOrderClaimStatus = z.infer<
  typeof productOrderClaimStatusSchema
>;

/** 상품 주문 상태 */
export const productOrderStatusSchema = z.enum([
  'NONE',
  'PAYMENT_AWAITING',
  'PAYMENT_FAILED',
  'PAYMENT_FAILED_TIMEOUT',
  'PAYMENT_COMPLETED',
  'PREPARING_FOR_DELIVERY',
  'IN_DELIVERY',
  'DELIVERED',
  'PURCHASE_ORDER_CONFIRMATION_ON_HOLD',
  'PURCHASE_ORDER_CONFIRMED',
]);
export type ProductOrderStatus = z.infer<typeof productOrderStatusSchema>;

/** 배송 타입 */
export const deliveryTypeSchema = z.enum([
  'NONE',
  'COURIER',
  'SELF',
  'E_COUPON',
  'INTANGIBLE_INSTANT',
  'INTANGIBLE_WITH_STATUS',
]);
export type DeliveryType = z.infer<typeof deliveryTypeSchema>;

/** 구매자 e쿠폰 클레임 가능 여부 */
export const eCouponBuyerClaimableStatusSchema = z.enum([
  'CANCELLABLE',
  'CLAIMABLE',
  'UNCLAIMABLE',
  'CANCELED',
  'CLAIM_REQUESTED',
]);
export type ECouponBuyerClaimableStatus = z.infer<
  typeof eCouponBuyerClaimableStatusSchema
>;

/**
 * 모바일 교환권 사용 표시 상태 (MKPC-3611)
 *
 * TODO(MKPC-3611): BE 머지 후 `pnpm typegen` 시 shoebill.ts의
 * `MarketProductOrderResponse['eCouponUsageStatus']`가 반영되면 동기화 확인.
 */
export const eCouponUsageStatusSchema = z.enum([
  'USABLE',
  'PARTIALLY_USED',
  'USED',
  'EXPIRED',
  'CANCELED',
]);
export type ECouponUsageStatus = z.infer<typeof eCouponUsageStatusSchema>;

/** 상품 구분 */
export const productTypeSchema = z.enum([
  'ACTUAL_PRODUCT',
  'ACTUAL_PRODUCT_1P',
  'E_COUPON',
  'ACTUAL_PRODUCT_1P_STOCK',
  'ADVERTISEMENT',
  'DATA_PRODUCT',
]);
export type ProductType = z.infer<typeof productTypeSchema>;

/** 과세 타입 */
export const taxationTypeSchema = z.enum([
  'NONE',
  'TAXATION',
  'TAXATION_FREE',
  'ZERO_TAX_RATE',
  'COMPLEX_TAXATION',
]);
export type TaxationType = z.infer<typeof taxationTypeSchema>;

/** 입점몰 판매 타입 */
export const vendorSalesTypeSchema = z.enum([
  'VENDOR_1P',
  'VENDOR_3P',
  'VENDOR_1P_STOCK',
  'VENDOR_ADVERTISEMENT',
  'VENDOR_DATA_PRODUCT',
]);
export type VendorSalesType = z.infer<typeof vendorSalesTypeSchema>;

// ============================================================================
// 공통 스키마
// ============================================================================

/**
 * 가격 정보 스키마
 */
export const priceSchema = z.object({
  /** 0이 아닌지 여부 */
  isNotZero: z.boolean(),
  /** 면세 여부 */
  isTaxFree: z.boolean(),
  /** 0 여부 */
  isZero: z.boolean(),
  /** 세금 제외 가격 */
  priceWithoutTax: z.number(),
  /** 세금 */
  tax: z.number(),
  /** 면세 금액 */
  taxFree: z.number(),
  /** 과세 금액 */
  taxable: z.number(),
  /** 세금 포함 과세 금액 */
  taxableWithTax: z.number(),
  /** 총액 */
  total: z.number(),
});
export type Price = z.infer<typeof priceSchema>;

/**
 * 선물 데이터 스키마 (OrderGiftDto)
 */
export const orderGiftSchema = z.object({
  /** 구매자 ID */
  buyerId: z.number(),
  /** 생성 일시 (KST) */
  createdAtKst: z.string(),
  /** 선물 메시지 */
  giftMessage: z.string(),
  /** 선물 ID */
  id: z.number().optional(),
  /** 선물 여부 */
  isGift: z.boolean(),
  /** 주문 ID */
  orderId: z.number(),
  /** 수령자명 */
  receiverName: z.string(),
  /** 수정 일시 (KST) */
  updatedAtKst: z.string(),
});
export type OrderGift = z.infer<typeof orderGiftSchema>;

/**
 * 상품내 입점몰 정보 스키마 (MarketProductVendorResponse)
 */
export const vendorSchema = z.object({
  /** 1P 입점몰 여부 (deprecated) */
  '1pVendor': z.boolean().optional(),
  /** 3P 입점몰 여부 (deprecated) */
  '3pVendor': z.boolean().optional(),
  /** 주소지 */
  address: z.string().optional(),
  /** AS 연락처 */
  asContact: z.string().optional(),
  /** 사업자등록번호 */
  businessNumber: z.string().optional(),
  /** 상세주소지 */
  detailAddress: z.string().optional(),
  /** 입점몰 ID */
  id: z.number().optional(),
  /** 1P 입점몰 여부 */
  is1pVendor: z.boolean().optional(),
  /** 3P 입점몰 여부 */
  is3pVendor: z.boolean().optional(),
  /** 통신판매업 신고번호 */
  mailOrderNumber: z.string().optional(),
  /** 입점몰명 */
  mallName: z.string().optional(),
  /** 대표자 */
  representativeName: z.string().optional(),
  /** 입점사 홈 이미지 URL */
  vendorHomeImageUrl: z.string().optional(),
  /** 입점사 로고 이미지 URL */
  vendorLogoImageUrl: z.string().optional(),
  /** 상호명 */
  vendorName: z.string().optional(),
  /** 입점몰 판매 타입 */
  vendorSalesType: vendorSalesTypeSchema.optional(),
  /** 우편번호 */
  zipCode: z.string().optional(),
});
export type OrderVendor = z.infer<typeof vendorSchema>;

// ============================================================================
// 상품 주문 스키마 (MarketProductOrderResponse)
// ============================================================================

/**
 * 상품 주문 스키마
 *
 * 깊은 중첩(discountItems 등 컴포넌트 미사용 필드)은 런타임 parse 실패 방지를 위해
 * z.unknown()으로 느슨하게 처리합니다.
 */
export const productOrderSchema = z.object({
  /** 실 결제 금액 (할인 후 - 포인트 사용액) */
  actualAmount: z.number(),
  /** 현금영수증 확인 URL */
  cashReceiptUrl: z.string().optional(),
  /** 클레임 상태 */
  claimStatus: productOrderClaimStatusSchema.optional(),
  /** 상품 주문 코드 */
  code: z.string().optional(),
  /** 배송 가능 여부 */
  deliverable: z.boolean().optional(),
  /** 배송비 묶음 그룹 ID */
  deliveryFeeBundleId: z.number().optional(),
  /** 배송 타입 */
  deliveryType: deliveryTypeSchema.optional(),
  /** 할인 아이템 목록 (컴포넌트 미사용 - 느슨하게 처리) */
  discountItems: z.array(z.unknown()),
  /** 구매자 e쿠폰 클레임 가능 여부 */
  eCouponBuyerClaimableStatus: eCouponBuyerClaimableStatusSchema.optional(),
  /** 모바일 교환권 사용 표시 상태 (MKPC-3611) */
  eCouponUsageStatus: eCouponUsageStatusSchema.optional(),
  /** e쿠폰 coupon_number */
  eCouponNumber: z.string().optional(),
  /** e쿠폰 seq_number */
  eCouponSeqNumber: z.string().optional(),
  /** 확정 적립 포인트 */
  earnedPoint: z.number(),
  /** 도착/출고 예상일 텍스트 */
  expectedDeliveryText: z.string().optional(),
  /** 상품 요율 */
  feeRate: z.number().optional(),
  /** 상품 주문 ID */
  id: z.number().optional(),
  /** 배송 여부 */
  isDeliverable: z.boolean().optional(),
  /** 마지막 클레임 ID */
  lastClaimId: z.number().optional(),
  /** 모바일 교환권 이미지 URL */
  mobileCouponImageUrl: z.string().optional(),
  /** 오퍼 ID */
  offerId: z.number().optional(),
  /** 옵션명 */
  optionName: z.string().optional(),
  /** 주문 ID */
  orderId: z.number().optional(),
  /** 배수별 주문 단위 */
  orderPerMultipleQuantity: z.number().optional(),
  /** 주문 개수 */
  orderQuantity: z.number().optional(),
  /** 결제수단 */
  paymentMethodType: paymentMethodTypeSchema.optional(),
  /** 상품 이미지 URL */
  productImageUrl: z.string().optional(),
  /** 상품명 */
  productName: z.string().optional(),
  /** 상품 1개 가격 */
  productPrice: z.number().optional(),
  /** 상품 1개 가격(부가세제외) */
  productPriceWithoutVat: z.number().optional(),
  /** 상품 구분 */
  productType: productTypeSchema,
  /** 정산 예정 금액(상품 총 비용) */
  settlementAmount: z.number().optional(),
  /** 상품 상태 */
  status: productOrderStatusSchema.optional(),
  /** 상품재고 ID */
  stockKeepingUnitId: z.number().optional(),
  /** 스마트 택배 API용 택배사 코드 */
  sweetTrackerCode: z.string().optional(),
  /** 과세 타입 */
  taxationType: taxationTypeSchema.optional(),
  /** 쿠폰 할인 총액 */
  totalCouponDiscountAmount: z.number().optional(),
  /** 총 할인금액 */
  totalDiscountAmount: z.number().optional(),
  /** 세금제외 총 할인금액 */
  totalDiscountAmountWithoutVat: z.number().optional(),
  /** 할인된 상품가격 총액 */
  totalDiscountedProductPrice: z.number().optional(),
  /** 플러스 멤버십 할인 총액 */
  totalPlusMembershipDiscountAmount: z.number().optional(),
  /** 세금제외 플러스 멤버십 할인 총액 */
  totalPlusMembershipDiscountAmountWithoutVat: z.number().optional(),
  /** 상품 총 가격 */
  totalProductPrice: z.number().optional(),
  /** 송장번호 */
  trackingNumber: z.string().optional(),
  /** 입점몰 정보 */
  vendor: vendorSchema.optional(),
  /** 입점몰 ID */
  vendorId: z.number().optional(),
  /** 입점몰명 */
  vendorName: z.string().optional(),
  /** 벤더주문 ID */
  vendorOrderId: z.number().optional(),
});
export type OrderProductItem = z.infer<typeof productOrderSchema>;

// ============================================================================
// 주문 스키마 (MarketOrderResponse)
// ============================================================================

/**
 * 주문 스키마
 *
 * 깊은 중첩(deliveryFeeBundles 등 컴포넌트 미사용 필드)은 런타임 parse 실패 방지를 위해
 * z.unknown()으로 느슨하게 처리합니다.
 */
export const orderSchema = z.object({
  /** 추가 배송비 금액 */
  additionalDeliveryFee: priceSchema.optional(),
  /** 구매자 전화번호 */
  buyerContact: z.string().optional(),
  /** 구매자 ID */
  buyerId: z.number().optional(),
  /** 구매자명 */
  buyerName: z.string().optional(),
  /** 주문 코드 */
  code: z.string().optional(),
  /** 배송비 묶음 리스트 (컴포넌트 미사용 - 느슨하게 처리) */
  deliveryFeeBundles: z.array(z.unknown()),
  /** 암호화된 주문 코드 */
  encryptedOrderCode: z.string().optional(),
  /** 선물 데이터 */
  gift: orderGiftSchema.optional(),
  /** 주문 ID */
  id: z.number().optional(),
  /** 모바일 교환권 이미지 URL */
  mobileCouponImageUrl: z.string().optional(),
  /** 주문날짜 (ISO-8601, KST) */
  orderDate: z.string().optional(),
  /** 결제수단 */
  paymentMethodType: paymentMethodTypeSchema.optional(),
  /** 상품 주문 리스트 */
  productOrders: z.array(productOrderSchema),
  /** 주문 상태 */
  status: orderStatusSchema.optional(),
  /** 실결제금액 */
  totalActualAmount: priceSchema.optional(),
  /** 추가 배송비 총액 */
  totalAdditionalDeliveryFee: z.number().optional(),
  /** 추가 배송비 부가가치세 */
  totalAdditionalDeliveryFeeVat: z.number().optional(),
  /** 세금제외한 추가 배송비 총액 */
  totalAdditionalDeliveryFeeWithoutVat: z.number().optional(),
  /** 총 주문금액 (할인적용 후) */
  totalAmountWithDiscount: priceSchema.optional(),
  /** 쿠폰 할인 총액 */
  totalCouponDiscountAmount: z.number().optional(),
  /** 제외될 쿠폰 할인 부가가치세 */
  totalCouponDiscountAmountVat: z.number().optional(),
  /** 제외될 세금 제외한 쿠폰 할인 비용 */
  totalCouponDiscountAmountWithoutVat: z.number().optional(),
  /** 배송비 총액 */
  totalDeliveryFee: z.number().optional(),
  /** 총 배송비 */
  totalDeliveryFeeAmount: priceSchema.optional(),
  /** 사용 포인트 배송비 */
  totalDeliveryFeePointAmount: priceSchema.optional(),
  /** 배송비 부가가치세 */
  totalDeliveryFeeVat: z.number().optional(),
  /** 세금제외한 배송비 총액 */
  totalDeliveryFeeWithoutVat: z.number().optional(),
  /** 총 할인금액 */
  totalDiscountAmount: priceSchema.optional(),
  /** 할인된 상품가격 총액 */
  totalDiscountedProductPrice: z.number().optional(),
  /** 할인된 상품가격 부가가치세 */
  totalDiscountedProductPriceVat: z.number().optional(),
  /** 할인된 세금제외한 상품가격 총액 */
  totalDiscountedProductPriceWithoutVat: z.number().optional(),
  /** 주문 금액 총액 */
  totalOrderAmount: z.number().optional(),
  /** 주문 금액 부가가치세 */
  totalOrderAmountVat: z.number().optional(),
  /** 세금제외한 주문 금액 총액 */
  totalOrderAmountWithoutVat: z.number().optional(),
  /** 플러스 멤버십 할인 총액 */
  totalPlusMembershipDiscountAmount: z.number().optional(),
  /** 플러스 멤버십 할인 부가가치세 */
  totalPlusMembershipDiscountAmountVat: z.number().optional(),
  /** 세금 제외 플러스 멤버십 할인 비용 */
  totalPlusMembershipDiscountAmountWithoutVat: z.number().optional(),
  /** 사용 포인트 (상품 가격 + 배송비) */
  totalPointAmount: priceSchema.optional(),
  /** 상품가격 총액 (배송비 제외) */
  totalProductAmount: priceSchema.optional(),
  /** 상품가격 총액 */
  totalProductPrice: z.number().optional(),
  /** 사용 포인트 상품 가격 */
  totalProductPricePointAmount: priceSchema.optional(),
  /** 상품가격 부가가치세 */
  totalProductPriceVat: z.number().optional(),
  /** 세금제외한 상품가격 총액 */
  totalProductPriceWithoutVat: z.number().optional(),
  /** 가상계좌 입금기한 */
  vacctInputDateTime: z.string().optional(),
});
export type Order = z.infer<typeof orderSchema>;

// ============================================================================
// 주문 내역 페이지 스키마 (CursorPagedResponse)
// ============================================================================

/**
 * 주문 내역 페이지 스키마 (무한 스크롤용 커서 페이징)
 */
export const orderHistoryPageSchema = z.object({
  /** 주문 목록 */
  content: z.array(orderSchema),
  /** 다음 페이지 여부 */
  hasNextPage: z.boolean(),
  /** 마지막 항목 ID (다음 페이지 요청 시 필요) */
  lastId: z.number().optional(),
  /** pagination value */
  searchAfter: z.array(z.string()).optional(),
  /** 조회된 응답 수 */
  size: z.number(),
  /** 정렬 타입 */
  sortType: z
    .enum(['SCORE', 'DISPLAY_RANK', 'LOW_PRICE', 'LATEST_PRODUCTS'])
    .optional(),
});
export type OrderHistoryPage = z.infer<typeof orderHistoryPageSchema>;

// ============================================================================
// 주문 상세 응답 스키마 (MarketOrderDetailsResponseV2)
// ============================================================================

/** 수령 위치 */
export const orderDeliveryLocationSchema = z.enum([
  'NONE',
  'INSIDE_STORE',
  'REFRIGERATOR',
  'CUSTOM',
]);
export type OrderDeliveryLocation = z.infer<
  typeof orderDeliveryLocationSchema
>;

/** 배송비 산정방식 */
export const orderDeliveryFeeTypeSchema = z.enum([
  'NONE',
  'FREE',
  'FIX',
  'CONDITIONALLY_FREE',
]);
export type OrderDeliveryFeeType = z.infer<typeof orderDeliveryFeeTypeSchema>;

/** 결제 상태 */
export const paymentStatusSchema = z.enum([
  'NONE',
  'AWAITING',
  'FAILED',
  'COMPLETED',
  'TIMEOUT',
  'TIMEOUT_AND_COMPLETED',
]);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

/** 주문 방식 */
export const paymentOrderTypeSchema = z.enum([
  'DEFAULT',
  'QUICK_ORDER',
  'PAY_LATER',
]);
export type PaymentOrderType = z.infer<typeof paymentOrderTypeSchema>;

/** 결제 수단 (결제 결과 응답용) */
export const paymentResultTypeSchema = z.enum([
  'NONE',
  'NONE_COUPON',
  'CARD',
  'CARD_COUPON',
  'INICIS_CARD',
  'INICIS_CARD_COUPON',
  'INICIS_VACCT',
  'INICIS_VACCT_COUPON',
  'KPN_NAVER',
  'KPN_NAVER_COUPON',
  'KPN_NAVER_CARD',
  'KPN_NAVER_CARD_COUPON',
  'KPN_NAVER_POINT',
  'KPN_NAVER_POINT_COUPON',
  'KPN_CARD',
  'KPN_CARD_COUPON',
  'KPN_VACCT',
  'KPN_VACCT_COUPON',
  'KPN_E_COUPON_CARD',
  'KPN_E_COUPON_CARD_COUPON',
  'POINT',
  'POINT_COUPON',
  'PAY_LATER',
  'PAY_LATER_COUPON',
]);
export type PaymentResultType = z.infer<typeof paymentResultTypeSchema>;

/** 할인 타입 (결제 결과 / 장바구니 공통) */
export const frontDiscountTypeSchema = z.enum([
  'COUPON',
  'MEMBERSHIP_EVENT',
  'MEMBERSHIP_OPTION_LUCKY_BONUS_PACK',
]);
export type FrontDiscountType = z.infer<typeof frontDiscountTypeSchema>;

/**
 * 주문 배송지 정보 스키마 (MarketDeliveryResponse)
 */
export const deliverySchema = z.object({
  /** 주소지 */
  address: z.string().optional(),
  /** 상호명 */
  businessName: z.string().optional(),
  /** 수령 위치 */
  deliveryLocation: orderDeliveryLocationSchema.optional(),
  /** 상세주소지 */
  detailAddress: z.string().optional(),
  /** 수령자 */
  recipient: z.string().optional(),
  /** 수령자 전화번호 */
  recipientPhoneNumber: z.string().optional(),
  /** 배송 요청사항 */
  requestForDelivery: z.string().optional(),
});
export type Delivery = z.infer<typeof deliverySchema>;

/**
 * 적용 할인 항목 스키마 (FrontDiscountItemResponse)
 */
export const frontDiscountItemSchema = z.object({
  /** 할인 설명 */
  description: z.string(),
  /** 할인 금액 */
  discountAmount: z.number(),
  /** 할인 타입 */
  discountType: frontDiscountTypeSchema,
});
export type FrontDiscountItem = z.infer<typeof frontDiscountItemSchema>;

/**
 * 쿠폰 사용 정보 스키마 (PaymentResultResponseCouponInfo)
 */
export const paymentCouponInfoSchema = z.object({
  /** 쿠폰 ID */
  id: z.number(),
  /** 쿠폰명 */
  name: z.string(),
});
export type PaymentCouponInfo = z.infer<typeof paymentCouponInfoSchema>;

/**
 * 결제 상품 기본 정보 스키마 (ProductInfo)
 */
export const paymentProductInfoSchema = z.object({
  /** 카테고리 ID 목록 */
  categoryIds: z.array(z.number()),
  /** 오퍼 ID */
  offerId: z.number(),
  /** 가격 */
  price: z.number(),
  /** 상품 코드 */
  productCode: z.string(),
  /** 상품명 */
  productName: z.string(),
  /** 입점몰 ID */
  vendorId: z.number(),
});
export type PaymentProductInfo = z.infer<typeof paymentProductInfoSchema>;

/**
 * 구매 상품 정보 스키마 (PaymentResultResponseProductInfo)
 */
export const paymentResultProductInfoSchema = z.object({
  /** 상품 정보 */
  product: paymentProductInfoSchema,
  /** 구매 수량 */
  quantity: z.number(),
});
export type PaymentResultProductInfo = z.infer<
  typeof paymentResultProductInfoSchema
>;

/**
 * 결제 결과 정보 스키마 (MarketPaymentResultResponse)
 */
export const paymentResultSchema = z.object({
  /** 추가 배송비 결제 금액 */
  additionalDeliveryFeePaymentAmount: z.number().optional(),
  /** @deprecated 쿠폰 할인 금액 */
  couponDiscountAmount: z.number().optional(),
  /** 쿠폰 사용 정보 (Amplitude 분석 용도) */
  couponInfos: z.array(paymentCouponInfoSchema),
  /** 결제 카드 이름 */
  creditCardName: z.string().optional(),
  /** 배송비 결제 금액 */
  deliveryFeePaymentAmount: z.number().optional(),
  /** 적용된 할인 항목 */
  discountItems: z.array(frontDiscountItemSchema),
  /** 확정 적립 포인트 */
  earnedPoints: z.number(),
  /** e쿠폰 여부 (legacy) */
  ecoupon: z.boolean().optional(),
  /** 암호화된 주문 코드 */
  encryptedOrderCode: z.string().optional(),
  /** 예상 적립 포인트 */
  expectedEarnedPoints: z.number(),
  /** 선물 데이터 */
  gift: orderGiftSchema.optional(),
  /** 할부 개월 (0이면 일시불) */
  installmentMonth: z.number().optional(),
  /** 데이터 상품 주문 여부 */
  isDataProductOrder: z.boolean(),
  /** e쿠폰 여부 */
  isECoupon: z.boolean().optional(),
  /** e쿠폰 실패 여부 */
  isMobileCouponFailed: z.boolean(),
  /** e쿠폰 실패 여부 (legacy) */
  mobileCouponFailed: z.boolean().optional(),
  /** e쿠폰 실패 메시지 */
  mobileCouponFailedMessage: z.string().optional(),
  /** 모바일 교환권 이미지 URL */
  mobileCouponImageUrl: z.string().optional(),
  /** 넛징용 멤버십 할인 금액 */
  nudgeMembershipDiscountAmount: z.number().optional(),
  /** 주문 코드 (Amplitude 분석 용도) */
  orderCode: z.string().optional(),
  /** 주문 방식 */
  orderType: paymentOrderTypeSchema.optional(),
  /** 주문자 이름 */
  orderer: z.string().optional(),
  /** 결제 상세 메시지 */
  paymentMessage: z.string().optional(),
  /** 결제 상태 */
  paymentStatus: paymentStatusSchema.optional(),
  /** 결제 수단 */
  paymentType: paymentResultTypeSchema.optional(),
  /** @deprecated 플러스 멤버십 할인 금액 */
  plusMembershipDiscountAmount: z.number().optional(),
  /** 주문 상품 갯수 */
  productCount: z.number().optional(),
  /** 상품 할인 금액 */
  productDiscountAmount: z.number().optional(),
  /** 구매 상품 정보 (Amplitude 분석 용도) */
  productInfos: z.array(paymentResultProductInfoSchema),
  /** 상품 결제 금액 (할인 전) */
  productOriginPaymentAmount: z.number().optional(),
  /** 상품 결제 금액 (할인 후) */
  productPaymentAmount: z.number().optional(),
  /** 누적 주문 횟수 */
  purchaseCount: z.number().optional(),
  /** 환불계좌 은행코드 */
  refundAccountBankCode: z.string().optional(),
  /** 환불계좌 은행명 */
  refundAccountBankName: z.string().optional(),
  /** 환불계좌 예금주 */
  refundAccountName: z.string().optional(),
  /** 환불계좌 번호 */
  refundAccountNumber: z.string().optional(),
  /** 주문 상품 중 대표 상품명 */
  representativeProductName: z.string().optional(),
  /** 총 결제 금액 */
  totalPaymentAmount: z.number().optional(),
  /** 총 포인트 사용 금액 */
  totalPointAmount: z.number().optional(),
  /** 가상계좌 계좌번호 */
  vacctAccountNumber: z.string().optional(),
  /** 가상계좌 은행코드 */
  vacctBankCode: z.string().optional(),
  /** 가상계좌 은행명 */
  vacctBankName: z.string().optional(),
  /** 가상계좌 입금기한 */
  vacctInputDateTime: z.string().optional(),
  /** 가상계좌 예금주명 */
  vacctName: z.string().optional(),
});
export type PaymentResult = z.infer<typeof paymentResultSchema>;

/**
 * 입점사 내 상품 주문 묶음 스키마 (MarketOrderDetailProductBundleResponse)
 */
export const orderDetailProductBundleSchema = z.object({
  /** 추가 배송비 */
  additionalDeliveryFee: z.number().optional(),
  /** 계산된 배송비 */
  calculatedDeliveryFee: z.number().optional(),
  /** 배송비 현금영수증 확인 URL */
  cashReceiptUrlDeliveryFee: z.string().optional(),
  /** 조건부 무료 조건 금액 */
  conditionalFreeAmount: z.number().optional(),
  /** 배송비 산정방식 */
  deliveryFeeType: orderDeliveryFeeTypeSchema.optional(),
  /** 배송 준비 기간(일) */
  deliveryPreparationPeriod: z.number().optional(),
  /** 배송 형태 */
  deliveryType: deliveryTypeSchema.optional(),
  /** 상품 주문 정보 리스트 */
  products: z.array(productOrderSchema),
  /** 입점사 명 */
  vendorName: z.string().optional(),
});
export type OrderDetailProductBundle = z.infer<
  typeof orderDetailProductBundleSchema
>;

/**
 * 입점사 상품 주문 묶음 스키마 (MarketVendorOrderDetailProductBundleResponse)
 */
export const vendorOrderDetailBundleSchema = z.object({
  /** 상품 주문 정보 리스트 */
  productOrders: z.array(orderDetailProductBundleSchema),
  /** 입점몰 ID */
  vendorId: z.number().optional(),
  /** 입점사명 */
  vendorName: z.string().optional(),
});
export type VendorOrderDetailBundle = z.infer<
  typeof vendorOrderDetailBundleSchema
>;

/**
 * 주문 상세 응답 스키마 (MarketOrderDetailsResponseV2)
 */
export const orderDetailSchema = z.object({
  /** 구매자 전화번호 */
  buyerContact: z.string().optional(),
  /** 구매자명 */
  buyerName: z.string().optional(),
  /** 주문 코드 */
  code: z.string().optional(),
  /** 주문 배송지 정보 */
  delivery: deliverySchema.optional(),
  /** 암호화된 주문 코드 */
  encryptedOrderCode: z.string().optional(),
  /** 선물 데이터 */
  gift: orderGiftSchema.optional(),
  /** 주문 ID */
  id: z.number().optional(),
  /** 모바일 교환권 이미지 URL */
  mobileCouponImageUrl: z.string().optional(),
  /** 주문날짜 (ISO-8601, KST) */
  orderDate: z.string().optional(),
  /** 결제 ID */
  paymentId: z.number().optional(),
  /** 결제 결과 정보 */
  paymentResult: paymentResultSchema.optional(),
  /** 상품 타입 */
  productType: productTypeSchema,
  /** 카드전표 확인 URL */
  receiptUrl: z.string().optional(),
  /** 입점사 상품 주문 리스트 */
  vendorOrders: z.array(vendorOrderDetailBundleSchema),
});
export type OrderDetail = z.infer<typeof orderDetailSchema>;
